import React, { useCallback } from 'react';
import { Button, makeStyles, Text, Toaster, ToastIntent, useId, useToastController } from '@fluentui/react-components';
import { FILE_STATUS, IDocumentDetails } from '../../../@types/models';
import { Document } from '@si/fib-icons';
import { t } from "i18next";
import { isOutlookApp } from '../../../utils/Helper';
import { openDocumentLink } from '../../../utils/openDocumentLink';
import { getPrerequisiteData } from '../../../Office/archiveFile/fileActionUtils';
import { CheckinStream, UndocheckOutSVC } from '../../../services/api/dataServiceOffice';
import { UploadChunkService } from '../../../services/api/Filechunkupload';
import { getToastMessageComponent } from '../ToastMessage';
import SpinnerComponent from '../controls/spinner';
import { ArchiveAgainMenu } from './ArchiveAgainMenu';
const ARCHIVE_CODE_IMPORT_ARCHIVE = 'Import archive';
const useStyles = makeStyles({
    ctnButton: { borderRadius: "4px", width: "100%", minHeight: "40px", whiteSpace: "nowrap" },
    detailBody: { minHeight: 'fit-content', width: '100%', justifyContent: "space-between", display: 'flex', flexDirection: "column" },
    containerDetails: { minHeight: 'fit-content' },
    containerHeaderDiv: { borderBottom: "1px solid #00000026" },
    containerHeaderItemHeading: { gap: "8px", fontSize: "16px", fontWeight: "600", padding: "8px", display: "flex" },
    containerHeaderItemLogo: {
        borderRadius: "100px", gap: "10px", backgroundColor: "#B5D9D94D",
        justifyContent: 'center', width: "42px", height: "42px", display: "flex"
    },
    containerHeaderLogo: { paddingTop: "10px", height: "20px" },
    containerHeaderItemNumber: { fontWeight: "600", fontSize: "14px", color: "rgba(66, 66, 66, 0.60)", fontStyle: "normal", lineHeight: "20px" },
    containerBodyDiv: { gap: "12px", display: 'flex', flexDirection: 'column', marginTop: "12px" },
    containerBodyFlex: { display: 'flex', flexDirection: 'column' },
    containerBodyTextHeading: { padding: "0px, 12px, 0px, 12px", fontSize: "16px", fontWeight: "600", gap: "10px" },
    containerBodyTextDetails: {
        padding: "0px, 12px, 0px, 12px", fontSize: "14px", gap: "10px",
        lineHeight: "20px", fontWeight: "400s", color: "#616161"
    },
    containerButton: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }
});

const ArchiveDetail: React.FC<IDocumentDetails> = (props: IDocumentDetails) => {
    const styles = useStyles();
    const openText = t('Open document in 360');
    const openAriaLabel = t('Open or register');
    const checkInText = t('Check-in file');
    const undotext = t('Undo check-out');
    const responsible = (props.ResponsibleName ? props.ResponsibleName + " - " : "") + props.OrgUnitName;
    const accessCodeAndParagraph = props.AccessCodeDesc + (props.Paragraph ? " / " + props.Paragraph : "");
    const toasterId = useId("toastIdDetailpage");
    const { dispatchToast, dismissToast } = useToastController(toasterId);
    const loadingText = t("Loading...");
    const [showSpinner, setShowSpinner] = React.useState(false);
    const [fileStatus, setFileStatus] = React.useState<FILE_STATUS>(props?.FileStatus ? props.FileStatus : FILE_STATUS.NORMAL);

    const showToast = useCallback((message: string, toastType: ToastIntent) => {
        setTimeout(() => {
            dispatchToast(
                getToastMessageComponent(message, () => dismissToast(toasterId), t("Dismiss")),
                { intent: toastType, timeout: -1 }
            );
        }, 100); // Small delay of 100ms
    }, [dispatchToast, dismissToast, toasterId]);

    const checkinFile = useCallback(() => {
        setShowSpinner(true);
        const prereqData = getPrerequisiteData();
        try {
            if (prereqData?.fileName) {
                UploadChunkService.uploadFileAndgetToken(prereqData.fileName, async (resData: string | null) => {

                    if (!resData) {
                        showToast(t("Upload failed"), "error");
                        setShowSpinner(false);
                        return;
                    }
                    const checkinRes = await CheckinStream(resData, "" + prereqData?.fileId);
                    if (checkinRes === "ok") {
                        setShowSpinner(false);
                        setFileStatus(FILE_STATUS.NORMAL);
                        showToast(t("Action completed successfully. Please close the file."), "warning");
                    } else {
                        showToast(t("Check-in failed"), "error");
                        setShowSpinner(false);
                    }

                }, (err: string) => {
                    showToast(err, "error");
                    setShowSpinner(false);
                });
            }

        }
        catch (exception) {
            setShowSpinner(false);
            showToast(exception as string, "error");
        }
    }, [getPrerequisiteData, showToast]);

    const undocheckOut = useCallback(async () => {
        setShowSpinner(true);
        const prereq = getPrerequisiteData();
        const response = await UndocheckOutSVC("" + prereq?.fileId);
        if (response === "ok") {
            showToast(t("Action completed successfully. Please close the file."), "warning");
            setFileStatus(FILE_STATUS.NORMAL);
        } else {
            showToast(t("Undo checkout failed"), "error");
        }
        setShowSpinner(false);
    }, [getPrerequisiteData, showToast]);
    return (
        <div className={styles.detailBody}>
            <div className={styles.containerDetails}>
                <div className={styles.containerHeaderDiv}>
                    <div className={styles.containerHeaderItemHeading}>
                        <div className={styles.containerHeaderItemLogo}>
                            <Document className={styles.containerHeaderLogo} size={16}></Document></div>
                        <div>
                            <div>{props.DocumentTitle}</div>
                            <div className={styles.containerHeaderItemNumber}>
                                {props.ArchiveCode + " : " + props.DocumentNumber}</div>
                        </div>
                    </div>
                </div>

                <div className={styles.containerBodyDiv}>
                    {props.CaseNumber && props.CaseDescription &&
                        <div className={styles.containerBodyFlex}>
                            <Text className={styles.containerBodyTextHeading}>{t('Case')}</Text>
                            <Text className={styles.containerBodyTextDetails}>{props.CaseNumber + " " + props.CaseDescription}</Text>
                        </div>
                    }
                    <div className={styles.containerBodyFlex}>
                        <Text className={styles.containerBodyTextHeading}>{t('Document Archive')}</Text>
                        <Text className={styles.containerBodyTextDetails}>{props.ArchiveCode}</Text>
                    </div>
                    <div className={styles.containerBodyFlex}>
                        <Text className={styles.containerBodyTextHeading}>{t('Document category')}</Text>
                        <Text className={styles.containerBodyTextDetails}>{props.CategoryCode}</Text>
                    </div>
                    <div className={styles.containerBodyFlex}>
                        <Text className={styles.containerBodyTextHeading}>{t('Responsible person')}</Text>
                        <Text className={styles.containerBodyTextDetails}>{responsible}</Text>
                    </div>
                    <div className={styles.containerBodyFlex}>
                        <Text className={styles.containerBodyTextHeading}>{t('Access code')}</Text>
                        <Text className={styles.containerBodyTextDetails}>{accessCodeAndParagraph}</Text>
                    </div>
                </div>
            </div>
            <div className={styles.containerButton}>
                <Button className={styles.ctnButton} appearance="primary" aria-label={openAriaLabel}
                    onClick={() => {
                        openDocumentLink(props.DocumentDetailsLink, props.DocumentRecno, props.ArchiveCode);
                    }}>
                    {props.ArchiveCode === ARCHIVE_CODE_IMPORT_ARCHIVE ? t('Register') : openText}
                </Button>
                {isOutlookApp() && <ArchiveAgainMenu buttonClassName={styles.ctnButton} />}
            </div>
            {!isOutlookApp() && fileStatus === FILE_STATUS.CHECKED_OUT ?
                <div className={styles.containerButton}>
                    <Button className={styles.ctnButton} aria-label={checkInText} onClick={checkinFile}>{checkInText}</Button>
                    <Button className={styles.ctnButton} aria-label={undotext} onClick={undocheckOut} >{undotext}</Button>
                </div>
                : null
            }
            <Toaster toasterId={toasterId} />
            <SpinnerComponent showSpinner={showSpinner} size='medium' label={loadingText} labelPosition='after' />
        </div>

    );
};

export default ArchiveDetail;
